import EventSummary from '@/components/event-summary/event-summary.view';
import { getZonedDateTimeFromLocaleString } from '@/timetime/timetime';
import { ICalendar } from '@/types/ICalendar';
import { ICalendarEvent } from '@/types/calendar-event/ICalendarEvent';
import { Box, Text } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Temporal } from 'temporal-polyfill';

interface IProps {
	calendar: ICalendar;
	dateTime: DateTime;
	events: Array<ICalendarEvent>;
	heading: string;
	onEventClick: (event: ICalendarEvent) => void;
	onEventDrop: (event: ICalendarEvent) => void;
}

export default function Day(props: IProps) {
	const [isDragging, setIsDragging] = useState(false);

	const handleDragEnter = () => {
		setIsDragging(true);
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault(); // Necesario para permitir el drop
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		const eventData = e.dataTransfer.getData('text/plain');
		const event = JSON.parse(eventData) as ICalendarEvent;
		const eventCopy = _updateEventDate(event, props.dateTime);
		if (eventCopy) {
			props.onEventDrop(eventCopy);
		}
		setIsDragging(false);
	};

	return (
		<Box
			h="full"
			overflow="auto"
			minW={'xs'}
			borderRightColor="gray.100"
			_first={{
				borderLeftWidth: 1,
				borderLeftColor: 'gray.100',
			}}
			onDragOver={handleDragOver}
			onDrop={handleDrop}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			transition="background-color 1s"
			bg={isDragging ? 'blue.100' : 'transparent'}
		>
			<Text textAlign="center" size="sm" borderBottomColor="gray.100" p={2}>
				{props.heading}
			</Text>

			<Box display="grid" gap={2} p={2}>
				{props.events.map((e) => {
					return (
						<EventSummary
							borderMode={props.calendar.tags.TT_BORDER_COLOR_BEHAVIOR ?? 'EVENT_STATUS'}
							displayDotOnPendingEvents={props.calendar.tags.TT_PENDING_EVENT_FEEDBACK === 'DOT'}
							onClick={props.onEventClick}
							key={e.id}
							event={e}
						/>
					);
				})}
			</Box>
		</Box>
	);
}

function _updateEventDate(event: ICalendarEvent, datetime: DateTime): ICalendarEvent | null {
	if (!_checkIfEventCanBeDropped(event)) {
		return null;
	}

	const newDate = Temporal.PlainDate.from(datetime.toISODate()!);

	const originalStart = getZonedDateTimeFromLocaleString(
		event.interval!.inclusiveStart!.dateTime!,
		event.interval!.inclusiveStart!.timeZone!
	);

	const originalEnd = getZonedDateTimeFromLocaleString(
		event.interval!.exclusiveEnd.dateTime!,
		event.interval!.exclusiveEnd.timeZone!
	);

	/**
	 * Dont do anything if event starts / ends on different days
	 */
	if (originalStart.day !== originalEnd.day) {
		console.info('Days differnt events');
		return null;
	}

	// Crear una copia del evento con fechas actualizadas
	const updatedEvent = {
		...event,
		interval: {
			inclusiveStart: {
				...event.interval!.inclusiveStart,
				dateTime: originalStart
					.with({ year: newDate.year, month: newDate.month, day: newDate.day })
					.toInstant()
					.toString({
						timeZone: event.interval!.inclusiveStart.timeZone!,
					}),
			},
			exclusiveEnd: {
				...event.interval!.exclusiveEnd,
				dateTime: originalEnd
					.with({ year: newDate.year, month: newDate.month, day: newDate.day })
					.toInstant()
					.toString({
						timeZone: event.interval!.exclusiveEnd.timeZone!,
					}),
			},
		},
	};

	return updatedEvent;
}

function _checkIfEventCanBeDropped(event: ICalendarEvent): boolean {
	// Comprobar si interval está definido y tiene las fechas de inicio y fin
	if (
		!event.interval?.inclusiveStart.dateTime ||
		!event.interval?.exclusiveEnd.dateTime ||
		!event.interval?.exclusiveEnd.timeZone ||
		!event.interval?.inclusiveStart.timeZone
	) {
		console.warn('El evento no tiene un intervalo definido o falta alguna fecha.');
		return false;
	}

	const originalStart = getZonedDateTimeFromLocaleString(
		event.interval.inclusiveStart.dateTime,
		event.interval.inclusiveStart.timeZone
	);
	const originalEnd = getZonedDateTimeFromLocaleString(
		event.interval.exclusiveEnd.dateTime,
		event.interval.exclusiveEnd.timeZone
	);

	/**
	 * Dont do anything if event starts / ends on different days
	 */
	if (originalStart.day !== originalEnd.day) {
		console.info('Days differnt events');
		return false;
	}

	return true;
}
